<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import NavBar from '@/components/NavBar.vue'
import PasskeyManagerModal from '@/components/PasskeyManagerModal.vue'
import { usePasskey } from '@/composables/usePasskey'

const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()
const { getCredentials, isSupported } = usePasskey()

const showPasskeyPromptModal = ref(false)

const checkPasskeySetup = async () => {
  // Don't show if user already skipped within 30 days
  const skipped = document.cookie.split('; ').some((c) => c.startsWith('passkey_setup_skipped='))
  if (skipped) return

  // Don't show if browser doesn't support passkeys
  if (!isSupported()) return

  // Show prompt only if user has no passkeys registered
  const result = await getCredentials()
  if (result.success && result.credentials.length === 0) {
    showPasskeyPromptModal.value = true
  }
}

onMounted(() => {
  checkPasskeySetup()
})

const logout = async () => {
  await authStore.logoutWithAPI()
  router.push('/login')
}

const isLeavingImpersonation = ref(false)
const stopImpersonating = async () => {
  if (isLeavingImpersonation.value) return
  isLeavingImpersonation.value = true
  try {
    const result = await authStore.stopImpersonation()
    const redirectMap: Record<string, string> = {
      partner: '/partners',
      company: '/companies',
      user: '/users',
    }
    const destination = result.targetType ? (redirectMap[result.targetType] ?? '/') : '/'
    router.push(destination)
  } finally {
    isLeavingImpersonation.value = false
  }
}
</script>

<template>
  <div class="flex flex-col h-screen bg-gray-50">
    <!-- Full-page loader for stop-impersonation -->
    <div v-if="isLeavingImpersonation" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div class="bg-white rounded-xl p-8 flex flex-col items-center gap-4 shadow-xl min-w-[260px]">
        <svg class="animate-spin h-10 w-10 text-purple-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <p class="text-gray-700 font-medium">{{ t('impersonation.exiting') }}</p>
      </div>
    </div>
    <!-- Impersonation banner — shown while a Super Admin is logged in as another account -->
    <div
      v-if="authStore.isImpersonating"
      class="flex items-center justify-center gap-3 bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow"
      role="alert"
    >
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
      <span>{{ t('impersonation.banner', { name: authStore.user?.name }) }}</span>
      <button
        type="button"
        :disabled="isLeavingImpersonation"
        @click="stopImpersonating"
        class="ml-2 rounded-md bg-white/20 px-3 py-1 font-semibold text-white transition-colors hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {{ isLeavingImpersonation ? t('impersonation.exiting') : t('impersonation.exit') }}
      </button>
    </div>
    <!-- Top Navigation Header -->
    <NavBar :logout="logout" />

    <!-- Main Content -->
    <main class="flex-1 overflow-scroll">
      <div class="p-4 sm:p-6 lg:p-8">
        <RouterView />
      </div>
    </main>
  </div>

  <!-- Passkey Setup Prompt (shown after login when no passkeys registered) -->
  <PasskeyManagerModal
    v-if="authStore.isManagementRole"
    :is-open="showPasskeyPromptModal"
    :prompt-mode="true"
    @close="showPasskeyPromptModal = false"
  />
</template>

<style scoped></style>
